import { getSupabaseBrowserClient } from "../integrations/supabase/client";
import { readPlatform } from "./platform";
import { clientEnv } from "../env";

/**
 * Web Push, from the browser's side.
 *
 * plan.md section 5.7: "Push permission is requested only after a user action, not on the first page
 * load." Nothing in this file runs on its own. `subscribeToPush` is called by a button and by
 * nothing else, and the permission prompt is the first thing inside it.
 *
 * The subscription is three values the browser generates: an endpoint at the push service, and two
 * keys the push service never sees. They go to `public.register_push_subscription`, which is the
 * gateway that already existed for them. The keys are what make the payload unreadable to Google or
 * Mozilla; putting them in our own database is the whole design and not a leak.
 */

export type PushState =
  | "unsupported"
  /**
   * The browser can receive notifications, but not from a tab.
   *
   * This is iOS and only iOS. plan.md section 14.10, "the only path where push works on iOS at all":
   * the installed home-screen application is "the only path where push works on iOS at all". An iOS
   * tab has no `PushManager`, so it fails the same check a browser with no push whatsoever fails,
   * and answering both with `unsupported` told every iPhone customer that their browser has no
   * notifications. It has them. It has a prerequisite.
   */
  | "needs-install"
  | "blocked"
  | "off"
  | "on"
  | "unconfigured";

export interface PushStatus {
  state: PushState;
  endpoint: string | null;
}

/** The five categories a person is opting into when they turn the device on (`report` since build round B2). */
const CONFIGURABLE = ["chat_reply", "request_update", "outage", "document", "report"] as const;

function supported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    typeof window !== "undefined" &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/**
 * Which of the two refusals this browser has earned.
 *
 * Called only once `supported()` has already said no, and it splits that no in two. An iOS tab is
 * told there is a step it can take, because there is one. Everything else is told the truth about
 * itself: this browser will not grow push, and the same information is still in the portal and in
 * the mail.
 *
 * An iPhone that has *already* been installed and still has no `PushManager` is answered
 * `unsupported` rather than `needs-install`. Home-screen web push arrived in iOS 16.4, so that
 * device is an older one, it has done everything the portal asked, and repeating the install advice
 * would be a loop it cannot get out of. The version is never read out of the user-agent string:
 * whether the capability is present is a fact the runtime answers, and the version string is a claim
 * Safari has frozen before.
 */
function unsupportedState(): PushState {
  const platform = readPlatform();

  return platform.isIos && !platform.isStandalone ? "needs-install" : "unsupported";
}

/**
 * The VAPID public key, as the raw bytes `pushManager.subscribe` wants.
 *
 * It ships in the bundle, which is correct: it is the public half, and the browser has to send it to
 * the push service to bind the subscription to us. A build without it cannot subscribe, and says so
 * rather than failing at the prompt.
 */
function applicationServerKey(): Uint8Array | null {
  const key = clientEnv.VITE_VAPID_PUBLIC_KEY;

  if (!key) {
    return null;
  }

  const padded = key.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(padded.padEnd(padded.length + ((4 - (padded.length % 4)) % 4), "="));
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
}

function keyOf(subscription: PushSubscription, name: "p256dh" | "auth"): string {
  const raw = subscription.getKey(name);

  if (!raw) {
    return "";
  }

  let binary = "";
  for (const byte of new Uint8Array(raw)) {
    binary += String.fromCharCode(byte);
  }

  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export async function readPushStatus(): Promise<PushStatus> {
  if (!supported()) {
    return { state: unsupportedState(), endpoint: null };
  }

  if (!applicationServerKey()) {
    return { state: "unconfigured", endpoint: null };
  }

  if (Notification.permission === "denied") {
    return { state: "blocked", endpoint: null };
  }

  const registration = await navigator.serviceWorker.getRegistration();
  const existing = await registration?.pushManager.getSubscription();

  if (!existing) {
    return { state: "off", endpoint: null };
  }

  try {
    const { data, error } = await getSupabaseBrowserClient().rpc("current_push_subscription_status", {
      p_endpoint: existing.endpoint
    });

    if (error || data !== true) {
      return { state: "off", endpoint: existing.endpoint };
    }

    return { state: "on", endpoint: existing.endpoint };
  } catch {
    return { state: "off", endpoint: existing.endpoint };
  }
}

/**
 * Asks, subscribes, registers, and turns the four configurable categories on.
 *
 * The last step is what makes the button mean what it says. `push_enabled` defaults to false on every
 * category, which is right for a channel that writes on a lock screen, but a person who has just
 * pressed "turn notifications on for this device" has said yes to exactly that. Leaving the
 * preferences off would have produced a device that is subscribed and silent.
 */
export async function subscribeToPush(): Promise<PushStatus> {
  if (!supported()) {
    return { state: unsupportedState(), endpoint: null };
  }

  const key = applicationServerKey();

  if (!key) {
    return { state: "unconfigured", endpoint: null };
  }

  // The prompt, and the first thing that happens after the press.
  const permission = await Notification.requestPermission();

  if (permission !== "granted") {
    return { state: permission === "denied" ? "blocked" : "off", endpoint: null };
  }

  const registration = await navigator.serviceWorker.ready;
  const fresh = () =>
    registration.pushManager.subscribe({
      // Required by every browser that implements push: a subscription that can deliver silently is
      // not one this project wants either.
      userVisibleOnly: true,
      applicationServerKey: key as BufferSource
    });

  const supabase = getSupabaseBrowserClient();
  const register = (held: PushSubscription) =>
    supabase.rpc("register_push_subscription", {
      p_endpoint: held.endpoint,
      p_p256dh_key: keyOf(held, "p256dh"),
      p_auth_key: keyOf(held, "auth"),
      // Nothing identifying. A family name for the browser is what `schema.md` asks for and it is
      // what helps somebody recognise a device in a list; the full user agent string is a fingerprint.
      p_user_agent_family: browserFamily()
    });

  let subscription = (await registration.pushManager.getSubscription()) ?? (await fresh());
  let { error } = await register(subscription);

  /*
    Build round 54, 2026-10-31: the subscription this browser held belonged to another account. The
    endpoint is the row's key and the gateway never hands one person's row to another, so it refuses
    with `PORTAL_PUSH_SUBSCRIPTION_CONFLICT`. Sign-out now unsubscribes, but a browser left by a sign-out
    from before that, or by a session that simply expired, still holds one. The browser drops it and
    asks the push service for a new endpoint, which no account holds, and registers once more. Once:
    a second conflict on a brand new endpoint is not this case, and it goes to the person as a refusal.
  */
  if (error && isSubscriptionConflict(error)) {
    await subscription.unsubscribe();
    subscription = await fresh();
    ({ error } = await register(subscription));
  }

  if (error) {
    await subscription.unsubscribe();
    throw error;
  }

  const tenantId = await currentTenantId();

  if (tenantId) {
    const mail = await currentMailChoices(tenantId);
    for (const category of CONFIGURABLE) {
      await supabase.rpc("update_notification_preference", {
        p_tenant_id: tenantId,
        p_category: category,
        p_push_enabled: true,
        // Untouched. Email is a separate choice with a separate default, and turning push on must
        // not quietly change it. The gateway writes both columns, so the person's own choice is read
        // first and sent back as it was; a category with no row yet keeps the column's default, true.
        // Until build round 41b (2026-10-12) this sent true for every category, which turned mail
        // back on for anyone who had switched it off.
        p_email_enabled: mail.get(category) ?? true
      });
    }
  }

  return { state: "on", endpoint: subscription.endpoint };
}

export async function unsubscribeFromPush(): Promise<PushStatus> {
  if (!supported()) {
    return { state: unsupportedState(), endpoint: null };
  }

  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();

  if (!subscription) {
    return { state: "off", endpoint: null };
  }

  const supabase = getSupabaseBrowserClient();

  // The database first. A browser that unsubscribed and failed to tell us would leave a row the
  // dispatcher keeps pushing to, and the push service would answer 410 forever.
  await supabase.rpc("revoke_push_subscription", { p_endpoint: subscription.endpoint });
  await subscription.unsubscribe();

  return { state: "off", endpoint: null };
}

/**
 * Binds the subscription this browser already holds to the session that has just started.
 *
 * **Why this exists, since 2026-09-13.** A subscription belongs to the session that registered it,
 * and the server delivers only while GoTrue still holds that session, so a device signed out by a
 * password change or from another device stops receiving notifications at once. Migration
 * `20260913000004` is that change. Its consequence for a device that is signed in again is that the
 * old binding is dead: without this call a person who signs out and back in on their own phone would
 * have a subscription the browser still reports as `on` and the server will never deliver to.
 *
 * It is also plan.md section 14.10's second always-forgotten detail: "The app verifies its
 * subscription on every launch and resubscribes silently." Nothing did until now.
 *
 * **It never asks for anything.** It runs only when permission has already been granted and the
 * browser already holds a subscription, so plan.md section 5.7's rule that push permission is
 * requested only after a user action is untouched: there is no prompt in this path, and no new
 * subscription is created. It changes no preference either, which is the one difference from
 * `subscribeToPush`.
 *
 * Every failure is swallowed. It is called from sign-in, and a sign-in must not fail because a
 * notification binding could not be written; what that costs is a device that stays silent until
 * the next launch or until the person presses the button in `Den här enheten`.
 */
export async function rebindPushSubscription(): Promise<boolean> {
  try {
    if (!supported() || !applicationServerKey() || Notification.permission !== "granted") {
      return false;
    }

    // `getRegistration` and not `ready`: `ready` waits for a worker that may never register in this
    // tab, and a sign-in must not hang on it.
    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = await registration?.pushManager.getSubscription();

    if (!subscription) {
      return false;
    }

    const { error } = await getSupabaseBrowserClient().rpc("register_push_subscription", {
      p_endpoint: subscription.endpoint,
      p_p256dh_key: keyOf(subscription, "p256dh"),
      p_auth_key: keyOf(subscription, "auth"),
      p_user_agent_family: browserFamily()
    });

    return !error;
  } catch {
    return false;
  }
}

/** The gateway's refusal of an endpoint another account registered, by its code. */
function isSubscriptionConflict(error: { message?: unknown }): boolean {
  return typeof error.message === "string" && error.message.includes("PORTAL_PUSH_SUBSCRIPTION_CONFLICT");
}

/** A coarse family, not a fingerprint. */
function browserFamily(): string {
  const agent = navigator.userAgent;

  if (/Edg\//.test(agent)) {
    return "Edge";
  }
  if (/Firefox\//.test(agent)) {
    return "Firefox";
  }
  if (/Chrome\//.test(agent)) {
    return "Chrome";
  }
  if (/Safari\//.test(agent)) {
    return "Safari";
  }

  return "Okänd";
}

/**
 * The person's mail choice per category at this client, as stored. A failed read throws, so the
 * preferences are left exactly as they are rather than written from a guess.
 */
async function currentMailChoices(tenantId: string): Promise<Map<string, boolean>> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase
    .from("notification_preferences")
    .select("category, email_enabled")
    .eq("tenant_id", tenantId);

  if (error) {
    throw error;
  }

  return new Map(((data ?? []) as Array<{ category: string; email_enabled: boolean }>).map((row) => [row.category, row.email_enabled]));
}

async function currentTenantId(): Promise<string | null> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.from("tenants").select("id").limit(1);

  if (error || !data || data.length === 0) {
    return null;
  }

  return (data[0] as { id: string }).id;
}
